import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, Loader2, Inbox, MessageSquare, Search, ArrowLeft, ShieldCheck, GraduationCap, Briefcase, Users, Plus, X, Settings, Trash2, LogOut, Building2, Paperclip, FileText, Image as ImageIcon, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const ROLE_ICON = {
    responsable: ShieldCheck,
    formateur: Briefcase,
    alternant: GraduationCap,
};
const ROLE_LABEL = {
    responsable: "Responsable",
    formateur: "Formateur",
    alternant: "Alternant",
};

const TYPE_DIRECT = "direct";
const TYPE_GROUP = "group";
const TYPE_TUTOR = "tutor";

export default function Messages() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { partnerId: routePartnerId } = useParams();

    const [tab, setTab] = useState("direct"); // 'direct' | 'groups' | 'tutors'
    const [niveauFilter, setNiveauFilter] = useState("1ere"); // default to 1ère to never mix years on first open
    const [threads, setThreads] = useState([]);
    const [groups, setGroups] = useState([]);
    const [tutorThreads, setTutorThreads] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Active conversation: { type, id }
    const [active, setActive] = useState(routePartnerId ? { type: TYPE_DIRECT, id: routePartnerId } : null);
    const [convo, setConvo] = useState(null); // either {partner, messages} or {group, messages} or {alternant, messages}
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [showContacts, setShowContacts] = useState(false);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [showGroupMembers, setShowGroupMembers] = useState(false);
    const [search, setSearch] = useState("");
    const messagesEndRef = useRef(null);

    const reloadAll = async () => {
        try {
            const [t, g, c, tt] = await Promise.all([
                api.get("/messages/threads").then(r => r.data).catch(() => []),
                api.get("/groups").then(r => r.data).catch(() => []),
                api.get("/messages/contacts").then(r => r.data).catch(() => []),
                api.get("/messages/tutor-threads").then(r => r.data).catch(() => []),
            ]);
            setThreads(t || []);
            setGroups(g || []);
            setContacts(c || []);
            setTutorThreads(tt || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        (async () => { setLoading(true); await reloadAll(); setLoading(false); })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!active) { setConvo(null); return; }
        (async () => {
            try {
                let data;
                if (active.type === TYPE_DIRECT) {
                    ({ data } = await api.get(`/messages/with/${active.id}`));
                } else if (active.type === TYPE_GROUP) {
                    ({ data } = await api.get(`/groups/${active.id}`));
                } else if (active.type === TYPE_TUTOR) {
                    ({ data } = await api.get(`/messages/tutor-thread/${active.id}`));
                }
                setConvo(data);
                await reloadAll();
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Conversation introuvable.");
                setActive(null);
            }
        })();
    }, [active]);

    // Poll every 8s
    useEffect(() => {
        if (!active) return;
        const t = setInterval(async () => {
            try {
                let data;
                if (active.type === TYPE_DIRECT) ({ data } = await api.get(`/messages/with/${active.id}`));
                else if (active.type === TYPE_GROUP) ({ data } = await api.get(`/groups/${active.id}`));
                else if (active.type === TYPE_TUTOR) ({ data } = await api.get(`/messages/tutor-thread/${active.id}`));
                setConvo(data);
            } catch { /* ignore */ }
        }, 8000);
        return () => clearInterval(t);
    }, [active]);

    const send = async (e, attachment = null) => {
        e?.preventDefault?.();
        if (!active) return;
        const text = body.trim();
        if (!text && !attachment) return;
        setSending(true);
        try {
            const payload = { body: text };
            if (attachment) {
                payload.attachment_path = attachment.path;
                payload.attachment_filename = attachment.filename;
                payload.attachment_content_type = attachment.content_type;
            }
            if (active.type === TYPE_DIRECT) {
                await api.post("/messages/send", { to_user_id: active.id, ...payload });
            } else if (active.type === TYPE_GROUP) {
                await api.post(`/groups/${active.id}/messages`, payload);
            } else if (active.type === TYPE_TUTOR) {
                await api.post(`/messages/tutor-thread/${active.id}`, payload);
            }
            setBody("");
            // Refresh conversation
            let data;
            if (active.type === TYPE_DIRECT) ({ data } = await api.get(`/messages/with/${active.id}`));
            else if (active.type === TYPE_GROUP) ({ data } = await api.get(`/groups/${active.id}`));
            else if (active.type === TYPE_TUTOR) ({ data } = await api.get(`/messages/tutor-thread/${active.id}`));
            setConvo(data);
            reloadAll();
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi.");
        } finally {
            setSending(false);
        }
    };

    const startDirect = (partnerId) => {
        setConvo(null);  // clear stale data to avoid shape-mismatch crash while loading
        setActive({ type: TYPE_DIRECT, id: partnerId });
        setShowContacts(false);
        setTab("direct");
        navigate(`/messages/${partnerId}`, { replace: true });
    };

    const openGroup = (groupId) => {
        setConvo(null);
        setActive({ type: TYPE_GROUP, id: groupId });
        setTab("groups");
        navigate("/messages", { replace: true });
    };

    const openTutorThread = (alternantId) => {
        setConvo(null);
        setActive({ type: TYPE_TUTOR, id: alternantId });
        setTab("tutors");
        navigate("/messages", { replace: true });
    };

    const filteredContacts = useMemo(() => {
        const s = search.trim().toLowerCase();
        let list = contacts;
        if (niveauFilter !== "all") {
            list = list.filter((c) => c.role === "alternant" && c.niveau === niveauFilter);
        }
        if (!s) return list;
        return list.filter((c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || c.role.toLowerCase().includes(s));
    }, [contacts, search, niveauFilter]);

    const leaveGroup = async (groupId) => {
        if (!window.confirm("Quitter ce groupe ?")) return;
        try {
            await api.post(`/groups/${groupId}/leave`);
            toast.success("Vous avez quitté le groupe");
            setActive(null);
            setConvo(null);
            setGroups((prev) => prev.filter((g) => g.id !== groupId));
            await reloadAll();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };
    const deleteGroup = async (groupId) => {
        if (!window.confirm("Supprimer définitivement ce groupe ? Tous les messages seront effacés. Action IRRÉVERSIBLE.")) return;
        try {
            await api.delete(`/groups/${groupId}`);
            toast.success("Groupe supprimé");
            setActive(null);
            setConvo(null);
            // Optimistic local removal so the sidebar refreshes instantly
            setGroups((prev) => prev.filter((g) => g.id !== groupId));
            await reloadAll();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const deleteDirectThread = async (partnerId) => {
        if (!window.confirm("Supprimer cette conversation ? Tous les messages échangés avec cette personne seront effacés. Action IRRÉVERSIBLE.")) return;
        try {
            await api.delete(`/messages/direct/${partnerId}`);
            toast.success("Conversation supprimée");
            setActive(null);
            setConvo(null);
            setThreads((prev) => prev.filter((t) => t.partner_id !== partnerId));
            await reloadAll();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><Loader2 className="animate-spin text-orange" /></div>;

    const isResponsable = user?.role === "responsable";

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto px-4 lg:px-8 py-6 w-full" data-testid="messages-page">
                <div className="flex items-center gap-3 mb-4">
                    <button onClick={() => navigate("/dashboard")} className="text-xs font-mono text-muted2 hover:text-ink inline-flex items-center gap-1">
                        <ArrowLeft size={12} /> Tableau de bord
                    </button>
                </div>

                <div className="grid lg:grid-cols-[320px_1fr] gap-3 h-[calc(100vh-220px)] min-h-[500px]">
                    {/* LEFT — sidebar */}
                    <aside className="panel flex flex-col overflow-hidden" data-testid="messages-threads-panel">
                        <div className="px-4 pt-4">
                            <div className="label-eyebrow">Messagerie</div>
                            <h2 className="h-display text-lg font-semibold flex items-center gap-1.5"><MessageSquare size={16} className="text-orange" /> Conversations</h2>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-rule mt-3 text-xs font-mono">
                            <button onClick={() => setTab("direct")} className={`flex-1 py-2.5 transition-colors ${tab === "direct" ? "text-orange border-b-2 border-orange font-semibold" : "text-muted2 hover:text-ink"}`} data-testid="messages-tab-direct">
                                Directs ({threads.length})
                            </button>
                            <button onClick={() => setTab("groups")} className={`flex-1 py-2.5 transition-colors ${tab === "groups" ? "text-orange border-b-2 border-orange font-semibold" : "text-muted2 hover:text-ink"}`} data-testid="messages-tab-groups">
                                Groupes ({groups.length})
                            </button>
                            {(isResponsable || tutorThreads.length > 0) && (
                                <button onClick={() => setTab("tutors")} className={`flex-1 py-2.5 transition-colors ${tab === "tutors" ? "text-orange border-b-2 border-orange font-semibold" : "text-muted2 hover:text-ink"}`} data-testid="messages-tab-tutors">
                                    Tuteurs ({tutorThreads.length})
                                </button>
                            )}
                        </div>

                        {/* New button */}
                        <div className="px-3 pt-3">
                            {tab === "direct" ? (
                                <button onClick={() => setShowContacts(true)} className="btn-primary w-full !text-xs !py-2" data-testid="messages-new-direct-button">
                                    <Plus size={12} /> Nouvelle conversation
                                </button>
                            ) : tab === "groups" ? (
                                <button onClick={() => setShowCreateGroup(true)} className="btn-primary w-full !text-xs !py-2" data-testid="messages-new-group-button">
                                    <Plus size={12} /> Créer un groupe
                                </button>
                            ) : null}
                        </div>

                        <div className="flex-1 overflow-y-auto mt-2">
                            {tab === "direct" && (threads.length === 0 ? (
                                <Empty icon={Inbox} text="Aucune conversation directe." cta="Cliquez sur « Nouvelle conversation »." />
                            ) : threads.slice().sort((a, b) => {
                                // Within directs, sort alternants by niveau (1ere -> 2eme -> unset) before falling back to recency
                                const rank = (t) => {
                                    if (t.partner_role !== "alternant") return 3;
                                    if (t.partner_niveau === "1ere") return 0;
                                    if (t.partner_niveau === "2eme") return 1;
                                    return 2;
                                };
                                const dr = rank(a) - rank(b);
                                if (dr !== 0) return dr;
                                return (b.last_at || "").localeCompare(a.last_at || "");
                            }).map(t => {
                                const Icon = ROLE_ICON[t.partner_role] || GraduationCap;
                                const isActive = active?.type === TYPE_DIRECT && active.id === t.partner_id;
                                const niveauLabel = t.partner_niveau === "1ere" ? "1ère" : t.partner_niveau === "2eme" ? "2ème" : null;
                                return (
                                    <button key={t.partner_id} onClick={() => startDirect(t.partner_id)} className={`w-full text-left px-4 py-3 border-b border-rule hover:bg-surface flex items-start gap-3 ${isActive ? "bg-surface border-l-4 border-l-orange" : ""}`} data-testid={`thread-${t.partner_id}`}>
                                        <div className="w-9 h-9 bg-orange/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-orange" /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-semibold truncate flex items-center gap-1.5">
                                                    {t.partner_name}
                                                    {niveauLabel && <span className="px-1 py-0 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-mono">{niveauLabel}</span>}
                                                </span>
                                                {t.unread > 0 && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-orange text-white shrink-0">{t.unread}</span>}
                                            </div>
                                            <div className="text-[10px] font-mono text-muted2 mt-0.5">{ROLE_LABEL[t.partner_role] || t.partner_role}</div>
                                            <div className={`text-xs mt-1 truncate ${t.unread ? "text-ink font-medium" : "text-muted2"}`}>{t.last_from_me && <span className="text-muted2">Vous : </span>}{t.last_body}</div>
                                        </div>
                                    </button>
                                );
                            }))}

                            {tab === "groups" && (groups.length === 0 ? (
                                <Empty icon={Users} text="Aucun groupe." cta="Créez votre premier groupe." />
                            ) : groups.map(g => {
                                const isActive = active?.type === TYPE_GROUP && active.id === g.id;
                                return (
                                    <button key={g.id} onClick={() => openGroup(g.id)} className={`w-full text-left px-4 py-3 border-b border-rule hover:bg-surface flex items-start gap-3 ${isActive ? "bg-surface border-l-4 border-l-orange" : ""}`} data-testid={`group-${g.id}`}>
                                        <div className="w-9 h-9 bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0"><Users size={16} /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-semibold truncate">{g.name}</span>
                                                {g.unread > 0 && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-orange text-white shrink-0">{g.unread}</span>}
                                            </div>
                                            <div className="text-[10px] font-mono text-muted2 mt-0.5">{g.members?.length || 0} membre(s){g.is_owner ? " · admin" : ""}</div>
                                            {g.last_message && (<div className={`text-xs mt-1 truncate ${g.unread ? "text-ink font-medium" : "text-muted2"}`}><span className="text-muted2">{g.last_message.from_name} : </span>{g.last_message.body}</div>)}
                                        </div>
                                    </button>
                                );
                            }))}

                            {tab === "tutors" && (tutorThreads.length === 0 ? (
                                <Empty icon={Building2} text="Aucun message de tuteur entreprise." cta={isResponsable ? "Vos tuteurs vous écriront via leur espace dédié." : "Le tuteur entreprise pourra vous écrire depuis son espace."} />
                            ) : tutorThreads.map(t => {
                                const isActive = active?.type === TYPE_TUTOR && active.id === t.alternant_id;
                                const isMe = t.alternant_id === user.id;
                                const label = isMe ? "Mon tuteur d'entreprise" : `Tuteur de ${t.alternant_name}`;
                                return (
                                    <button key={t.alternant_id} onClick={() => openTutorThread(t.alternant_id)} className={`w-full text-left px-4 py-3 border-b border-rule hover:bg-surface flex items-start gap-3 ${isActive ? "bg-surface border-l-4 border-l-orange" : ""}`} data-testid={`tutor-thread-${t.alternant_id}`}>
                                        <div className="w-9 h-9 bg-blue-50 text-blue-700 border border-blue-200 flex items-center justify-center shrink-0"><Building2 size={16} /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-semibold truncate">{label}</span>
                                                {t.unread > 0 && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-orange text-white shrink-0">{t.unread}</span>}
                                            </div>
                                            <div className="text-[10px] font-mono text-muted2 mt-0.5">Code {t.tutor_code}</div>
                                            <div className={`text-xs mt-1 truncate ${t.unread ? "text-ink font-medium" : "text-muted2"}`}>{t.last_from_me && <span className="text-muted2">Vous : </span>}{t.last_body}</div>
                                        </div>
                                    </button>
                                );
                            }))}
                        </div>
                    </aside>

                    {/* RIGHT — conversation pane */}
                    <section className="panel flex flex-col overflow-hidden" data-testid="messages-conversation-pane">
                        {!convo ? (
                            <div className="flex-1 flex items-center justify-center text-center px-6">
                                <div>
                                    <MessageSquare size={48} className="mx-auto text-muted2 mb-3" strokeWidth={1.2} />
                                    <h3 className="h-display text-lg font-semibold">Sélectionnez une conversation</h3>
                                    <p className="text-xs text-muted2 mt-1.5 max-w-sm">Choisissez à gauche, ou démarrez-en une nouvelle.</p>
                                </div>
                            </div>
                        ) : (
                            <ConversationPane
                                active={active}
                                convo={convo}
                                user={user}
                                body={body}
                                setBody={setBody}
                                send={send}
                                sending={sending}
                                messagesEndRef={messagesEndRef}
                                onLeaveGroup={() => leaveGroup(active.id)}
                                onDeleteGroup={() => deleteGroup(active.id)}
                                onDeleteDirect={() => deleteDirectThread(active.id)}
                                onShowMembers={() => setShowGroupMembers(true)}
                                contacts={contacts}
                                onReloadAll={reloadAll}
                            />
                        )}
                    </section>
                </div>
            </main>
            <Footer />

            {showGroupMembers && active?.type === TYPE_GROUP && convo?.group && (
                <GroupMembersModal
                    group={convo.group}
                    contacts={contacts}
                    user={user}
                    onClose={() => setShowGroupMembers(false)}
                    onUpdated={async () => { setShowGroupMembers(false); await reloadAll(); openGroup(active.id); }}
                />
            )}

            {showContacts && (
                <ContactsModal
                    contacts={filteredContacts}
                    search={search}
                    setSearch={setSearch}
                    niveauFilter={niveauFilter}
                    setNiveauFilter={setNiveauFilter}
                    onClose={() => setShowContacts(false)}
                    onSelect={startDirect}
                />
            )}

            {showCreateGroup && (
                <CreateGroupModal
                    contacts={contacts}
                    onClose={() => setShowCreateGroup(false)}
                    onCreated={async (group) => {
                        setShowCreateGroup(false);
                        await reloadAll();
                        openGroup(group.id);
                    }}
                />
            )}
        </div>
    );
}

const Empty = ({ icon: Icon, text, cta }) => (
    <div className="p-6 text-center text-xs text-muted2 font-mono">
        <Icon className="mx-auto mb-2 text-muted2" size={28} />
        {text}<br />{cta}
    </div>
);

function ConversationPane({ active, convo, user, body, setBody, send, sending, messagesEndRef, onLeaveGroup, onDeleteGroup, onShowMembers }) {
    let title = ""; let subtitle = ""; let Icon = MessageSquare;
    let messages = convo.messages || [];
    let toolbar = null;

    // Defensive guard: if active type doesn't match convo shape (e.g. briefly
    // during switch), show a lightweight loading state instead of crashing.
    const shapeOk = (
        (active.type === TYPE_DIRECT && convo.partner) ||
        (active.type === TYPE_GROUP && convo.group) ||
        (active.type === TYPE_TUTOR && convo.alternant)
    );
    if (!shapeOk) {
        return (
            <div className="flex-1 flex items-center justify-center text-center px-6" data-testid="conversation-loading">
                <div>
                    <Loader2 size={28} className="mx-auto text-muted2 mb-2 animate-spin" />
                    <p className="text-xs text-muted2 font-mono">Chargement de la conversation…</p>
                </div>
            </div>
        );
    }

    if (active.type === TYPE_DIRECT) {
        title = convo.partner.name;
        subtitle = `${ROLE_LABEL[convo.partner.role] || convo.partner.role} · ${convo.partner.email}`;
        Icon = ROLE_ICON[convo.partner.role] || GraduationCap;
    } else if (active.type === TYPE_GROUP) {
        title = convo.group.name;
        subtitle = `${(convo.group.members || []).length} membre(s)`;
        Icon = Users;
        toolbar = (
            <div className="flex items-center gap-1.5">
                <button onClick={onShowMembers} className="p-1.5 hover:bg-orange/10 text-orange" title="Gérer les membres" data-testid="group-members-button"><Users size={14} /></button>
                {convo.group.is_owner ? (
                    <button onClick={onDeleteGroup} className="p-1.5 hover:bg-destructive/10 text-destructive" title="Supprimer le groupe" data-testid="group-delete-button"><Trash2 size={14} /></button>
                ) : (
                    <button onClick={onLeaveGroup} className="p-1.5 hover:bg-amber-50 text-amber-700" title="Quitter le groupe" data-testid="group-leave-button"><LogOut size={14} /></button>
                )}
            </div>
        );
    } else if (active.type === TYPE_TUTOR) {
        const isMe = convo.alternant.id === user.id;
        title = isMe ? "Mon tuteur d'entreprise" : `Tuteur de ${convo.alternant.name}`;
        subtitle = `Code ${convo.alternant.tutor_code} · échange via l'espace tuteur`;
        Icon = Building2;
    }

    return (
        <>
            <div className="px-5 py-4 border-b border-rule flex items-center gap-3">
                <div className="w-10 h-10 bg-orange/15 flex items-center justify-center shrink-0"><Icon size={18} className="text-orange" /></div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate" data-testid="conversation-partner-name">{title}</div>
                    <div className="text-[11px] font-mono text-muted2 mt-0.5 truncate">{subtitle}</div>
                </div>
                {toolbar}
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-2.5 bg-surface" data-testid="conversation-messages">
                {messages.length === 0 && <p className="text-center text-xs text-muted2 font-mono py-8">Aucun message. Écrivez le premier !</p>}
                {messages.map((m) => {
                    const fromMe = active.type === TYPE_DIRECT
                        ? m.from_user_id === user.id
                        : active.type === TYPE_GROUP
                            ? m.from_user_id === user.id
                            : m.from !== "tutor";
                    const senderName = !fromMe && active.type === TYPE_GROUP ? m.from_name : null;
                    const isBulletin = m.kind === "bulletin";
                    return (
                        <div key={m.id} className={`flex ${fromMe ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                            <div className={`max-w-[80%] px-3.5 py-2 ${isBulletin ? "border-2 border-orange bg-orange/5 text-ink" : (fromMe ? "bg-orange text-white" : "bg-paper border border-rule")}`}>
                                {isBulletin && (
                                    <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wider font-bold text-orange">
                                        📊 Bulletin officiel
                                    </div>
                                )}
                                {senderName && <div className="text-[10px] font-mono text-orange mb-1">{senderName}</div>}
                                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                                {!isBulletin && m.attachment_path && (
                                    <MessageAttachment
                                        path={m.attachment_path}
                                        filename={m.attachment_filename}
                                        contentType={m.attachment_content_type}
                                        fromMe={fromMe}
                                    />
                                )}
                                {isBulletin && m.attachment_path && (
                                    <a
                                        href={`${window.location.origin}/api/files/${m.attachment_path}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink text-white text-xs font-medium hover:bg-ink/80 transition-colors"
                                    >
                                        📎 Télécharger {m.attachment_filename || "le bulletin PDF"}
                                    </a>
                                )}
                                <div className={`text-[10px] mt-1 font-mono flex items-center gap-1.5 ${isBulletin ? "text-muted2" : (fromMe ? "text-white/70" : "text-muted2")}`}>
                                    <span>{new Date(m.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                                    {fromMe && active.type === TYPE_DIRECT && (
                                        m.read_at ? (
                                            <span className="inline-flex items-center gap-0.5" title={`Vu le ${new Date(m.read_at).toLocaleString("fr-FR")}`}>
                                                <CheckCheck size={11} /> Vu
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-0.5 opacity-60" title="Non lu">
                                                <Check size={11} />
                                            </span>
                                        )
                                    )}
                                    {fromMe && active.type === TYPE_GROUP && (() => {
                                        // Count everyone who read it, except the author
                                        const readers = (m.read_by || []).filter((uid) => uid !== m.from_user_id);
                                        const memberCount = Math.max(0, (active.group?.members || []).length - 1);
                                        if (readers.length === 0) return (
                                            <span className="inline-flex items-center gap-0.5 opacity-60" title="Non lu"><Check size={11} /></span>
                                        );
                                        if (readers.length >= memberCount && memberCount > 0) return (
                                            <span className="inline-flex items-center gap-0.5" title="Vu par tout le groupe"><CheckCheck size={11} /> Vu</span>
                                        );
                                        return (
                                            <span className="inline-flex items-center gap-0.5" title={`Vu par ${readers.length} sur ${memberCount}`}>
                                                <CheckCheck size={11} /> Vu {readers.length}/{memberCount}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
            <MessageComposer body={body} setBody={setBody} send={send} sending={sending} messagesEndRef={messagesEndRef} />
        </>
    );
}

function MessageComposer({ body, setBody, send, sending }) {
    const [pending, setPending] = useState(null);  // { path, filename, content_type, size }
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Pièce jointe trop volumineuse (max 10 Mo).");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/messages/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPending(data);
            toast.success("Pièce jointe prête à envoyer");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi.");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        await send(e, pending);
        setPending(null);
    };

    return (
        <form onSubmit={handleSubmit} className="p-3 border-t border-rule bg-paper" data-testid="message-input-form">
            {pending && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-orange/10 border border-orange/30 text-xs" data-testid="message-attachment-preview">
                    {pending.content_type?.startsWith("image/") ? <ImageIcon size={14} className="text-orange shrink-0" /> : <FileText size={14} className="text-orange shrink-0" />}
                    <span className="flex-1 truncate font-mono">{pending.filename}</span>
                    <span className="text-muted2 font-mono text-[10px]">{Math.round(pending.size / 1024)} Ko</span>
                    <button type="button" onClick={() => setPending(null)} className="text-muted2 hover:text-destructive" data-testid="message-attachment-remove">
                        <X size={14} />
                    </button>
                </div>
            )}
            <div className="flex items-end gap-2">
                <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={onPickFile} className="hidden" data-testid="message-file-input" />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || sending || !!pending}
                    title="Joindre un fichier ou une photo"
                    className="p-2 hover:bg-surface text-muted2 hover:text-orange transition-colors"
                    data-testid="message-attach-button"
                >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                </button>
                <textarea
                    rows={2}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
                    placeholder={pending ? "Ajouter un message (optionnel)…" : "Écrivez votre message…"}
                    className="field-input flex-1 resize-none !py-2"
                    data-testid="message-input"
                />
                <button type="submit" disabled={(!body.trim() && !pending) || sending} className="btn-primary !py-3" data-testid="message-send-button">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
            </div>
        </form>
    );
}

function MessageAttachment({ path, filename, contentType, fromMe }) {
    const url = `${window.location.origin}/api/files/${path}`;
    const isImage = contentType?.startsWith("image/");
    if (isImage) {
        return (
            <a href={url} target="_blank" rel="noreferrer" className="mt-2 block" data-testid="message-attachment-image">
                <img src={url} alt={filename || "Photo"} className="max-w-full max-h-64 rounded-sm border border-rule" loading="lazy" />
            </a>
        );
    }
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={`mt-2 inline-flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${fromMe ? "bg-white/15 hover:bg-white/25 text-white" : "bg-ink text-white hover:bg-ink/80"}`}
            data-testid="message-attachment-file"
        >
            <FileText size={14} /> {filename || "Pièce jointe"}
        </a>
    );
}

function ContactsModal({ contacts, search, setSearch, niveauFilter, setNiveauFilter, onClose, onSelect }) {
    const NIVEAU_TABS = [
        { key: "all", label: "Tous" },
        { key: "1ere", label: "1ère année BTS" },
        { key: "2eme", label: "2ème année BTS" },
    ];
    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-2 sm:p-4" data-testid="contacts-modal" onClick={onClose}>
            <div className="bg-paper max-w-lg w-full h-[92vh] sm:h-auto sm:max-h-[80vh] flex flex-col border border-rule min-h-0" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 sm:p-5 border-b border-rule shrink-0">
                    <div className="label-eyebrow">Nouvelle conversation</div>
                    <h2 className="h-display text-xl font-semibold mt-1">Membres de votre établissement</h2>
                    <div className="mt-3 relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="field-input !pl-8" autoFocus data-testid="contacts-search" />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3" data-testid="contacts-niveau-filter">
                        {NIVEAU_TABS.map((t) => {
                            const active = niveauFilter === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setNiveauFilter(t.key)}
                                    className={`px-3 py-1 text-xs transition-colors ${active ? "bg-ink text-white border border-ink" : "bg-paper border border-rule hover:border-ink/40"}`}
                                    data-testid={`contacts-niveau-tab-${t.key}`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto scroll-visible min-h-0">
                    {contacts.length === 0 ? <div className="p-8 text-center text-xs text-muted2 font-mono">Aucun contact.</div> : contacts.map(c => {
                        const Icon = ROLE_ICON[c.role] || GraduationCap;
                        const niveauLabel = c.niveau === "1ere" ? "1ère" : c.niveau === "2eme" ? "2ème" : null;
                        return (
                            <button key={c.id} onClick={() => onSelect(c.id)} className="w-full text-left px-5 py-3 hover:bg-surface border-b border-rule flex items-center gap-3" data-testid={`contact-${c.id}`}>
                                <div className="w-9 h-9 bg-orange/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-orange" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate flex items-center gap-2">
                                        {c.name}
                                        {niveauLabel && <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{niveauLabel}</span>}
                                    </div>
                                    <div className="text-[11px] font-mono text-muted2">{ROLE_LABEL[c.role] || c.role} · {c.email}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function CreateGroupModal({ contacts, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [selected, setSelected] = useState([]);
    const [search, setSearch] = useState("");
    const [niveauFilter, setNiveauFilter] = useState("1ere"); // default to a specific year, never mix
    const [creating, setCreating] = useState(false);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        let list = contacts;
        if (niveauFilter !== "all") {
            list = list.filter((c) => c.role === "alternant" && c.niveau === niveauFilter);
        }
        if (!s) return list;
        return list.filter((c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s));
    }, [contacts, search, niveauFilter]);

    const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    // Quick-select helpers: add/remove all alternants of a given year
    const quickSelect = (niv) => {
        const ids = contacts.filter((c) => c.role === "alternant" && c.niveau === niv).map((c) => c.id);
        if (ids.length === 0) {
            toast.info(`Aucun alternant ${niv === "1ere" ? "en 1ère année" : "en 2ème année"} dans votre établissement. Définissez le niveau dans /admin/users ou /formateurs.`);
            return;
        }
        // Toggle: if all are already selected, unselect; else, add missing
        const allSelected = ids.every((id) => selected.includes(id));
        if (allSelected) {
            setSelected(prev => prev.filter((id) => !ids.includes(id)));
        } else {
            setSelected(prev => Array.from(new Set([...prev, ...ids])));
        }
        // Also auto-suggest a group name if empty
        if (!name.trim()) {
            setName(niv === "1ere" ? "BTS Élec · 1ère année" : "BTS Élec · 2ème année");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error("Donnez un nom au groupe."); return; }
        if (selected.length === 0) { toast.error("Sélectionnez au moins un membre."); return; }
        setCreating(true);
        try {
            const { data } = await api.post("/groups", { name: name.trim(), member_ids: selected });
            toast.success(`Groupe « ${data.name} » créé avec ${data.members.length} membre(s)`);
            onCreated(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        } finally { setCreating(false); }
    };

    const NIVEAU_TABS = [
        { key: "all", label: "Tous" },
        { key: "1ere", label: "1ère année BTS" },
        { key: "2eme", label: "2ème année BTS" },
    ];

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-2 sm:p-4" data-testid="create-group-modal" onClick={onClose}>
            <div className="bg-paper max-w-lg w-full h-[92vh] sm:h-auto sm:max-h-[85vh] flex flex-col border border-rule" onClick={(e) => e.stopPropagation()}>
                <form onSubmit={submit} className="flex flex-col h-full min-h-0">
                    <div className="p-4 sm:p-5 border-b border-rule shrink-0">
                        <div className="label-eyebrow">Nouveau groupe</div>
                        <h2 className="h-display text-xl font-semibold mt-1">Créer un groupe de discussion</h2>
                        <div className="mt-3">
                            <label className="label-eyebrow mb-2 block">Nom du groupe *</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : BTS Élec 2A · Promo 2026 · Équipe pédagogique…" className="field-input" required maxLength={120} autoFocus data-testid="create-group-name-input" />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            <button type="button" onClick={() => quickSelect("1ere")} className="px-3 py-1.5 text-xs bg-orange/10 text-orange border border-orange/30 hover:bg-orange/20" data-testid="create-group-quick-1ere">
                                ⚡ Tous les 1ère année
                            </button>
                            <button type="button" onClick={() => quickSelect("2eme")} className="px-3 py-1.5 text-xs bg-orange/10 text-orange border border-orange/30 hover:bg-orange/20" data-testid="create-group-quick-2eme">
                                ⚡ Tous les 2ème année
                            </button>
                        </div>
                        <div className="mt-3 relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
                            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher des membres…" className="field-input !pl-8" data-testid="create-group-search" />
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="create-group-niveau-filter">
                            {NIVEAU_TABS.map((t) => {
                                const active = niveauFilter === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        onClick={() => setNiveauFilter(t.key)}
                                        className={`px-2.5 py-1 text-[11px] transition-colors ${active ? "bg-ink text-white border border-ink" : "bg-paper border border-rule hover:border-ink/40"}`}
                                        data-testid={`create-group-niveau-tab-${t.key}`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-2 text-[11px] font-mono text-muted2">{selected.length} membre(s) sélectionné(s) · vous serez ajoutée automatiquement</div>
                    </div>
                    <div className="flex-1 overflow-y-auto scroll-visible min-h-0">
                        {filtered.length === 0 ? <div className="p-8 text-center text-xs text-muted2 font-mono">Aucun contact.</div> : filtered.map(c => {
                            const Icon = ROLE_ICON[c.role] || GraduationCap;
                            const isSelected = selected.includes(c.id);
                            const niveauLabel = c.niveau === "1ere" ? "1ère" : c.niveau === "2eme" ? "2ème" : null;
                            return (
                                <button type="button" key={c.id} onClick={() => toggle(c.id)} className={`w-full text-left px-5 py-3 border-b border-rule flex items-center gap-3 ${isSelected ? "bg-orange/10" : "hover:bg-surface"}`} data-testid={`group-member-${c.id}`}>
                                    <div className={`w-5 h-5 border-2 flex items-center justify-center ${isSelected ? "bg-orange border-orange" : "border-rule"}`}>
                                        {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                                    </div>
                                    <div className="w-9 h-9 bg-orange/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-orange" /></div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate flex items-center gap-2">
                                            {c.name}
                                            {niveauLabel && <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{niveauLabel}</span>}
                                        </div>
                                        <div className="text-[11px] font-mono text-muted2">{ROLE_LABEL[c.role] || c.role} · {c.email}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <div className="p-3 sm:p-4 border-t border-rule flex justify-end gap-2 bg-paper shrink-0">
                        <button type="button" onClick={onClose} className="btn-outline !text-xs">Annuler</button>
                        <button type="submit" disabled={creating || !name.trim() || selected.length === 0} className="btn-primary !text-xs" data-testid="create-group-submit">
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            Créer le groupe ({selected.length + 1} membres)
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function GroupMembersModal({ group, contacts, user, onClose, onUpdated }) {
    // Current member IDs (excluding the owner who is always included)
    const initialIds = (group.members || []).filter((id) => id !== group.created_by);
    const [selected, setSelected] = useState(initialIds);
    const [search, setSearch] = useState("");
    const [niveauFilter, setNiveauFilter] = useState("all");
    const [saving, setSaving] = useState(false);

    const isOwner = group.is_owner;

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        // Exclude the current user (owner) from the picker; they are implicit
        let list = contacts.filter((c) => c.id !== group.created_by);
        if (niveauFilter !== "all") {
            list = list.filter((c) => c.role === "alternant" && c.niveau === niveauFilter);
        }
        if (!s) return list;
        return list.filter((c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s));
    }, [contacts, search, niveauFilter, group.created_by]);

    const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const submit = async (e) => {
        e.preventDefault();
        if (!isOwner) return;
        setSaving(true);
        try {
            await api.put(`/groups/${group.id}/members`, { member_ids: selected });
            toast.success(`Membres mis à jour (${selected.length + 1} au total)`);
            onUpdated();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const NIVEAU_TABS = [
        { key: "all", label: "Tous" },
        { key: "1ere", label: "1ère année" },
        { key: "2eme", label: "2ème année" },
    ];

    // Build a quick map for member detail (when not in contacts e.g. owner)
    const detailById = useMemo(() => {
        const m = {};
        (group.members_detail || []).forEach((d) => { m[d.id] = d; });
        return m;
    }, [group.members_detail]);

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-2 sm:p-4" data-testid="group-members-modal" onClick={onClose}>
            <div className="bg-paper max-w-lg w-full h-[92vh] sm:h-auto sm:max-h-[85vh] flex flex-col border border-rule" onClick={(e) => e.stopPropagation()}>
                <form onSubmit={submit} className="flex flex-col h-full min-h-0">
                    <div className="p-4 sm:p-5 border-b border-rule shrink-0">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="label-eyebrow">Membres du groupe</div>
                                <h2 className="h-display text-xl font-semibold mt-1">{group.name}</h2>
                                <p className="text-xs text-muted2 mt-1">
                                    {isOwner ? "Cochez/décochez pour ajouter ou retirer des membres." : "Vous voyez les membres mais seul le créateur peut les modifier."}
                                </p>
                            </div>
                            <button type="button" onClick={onClose} className="p-1.5 hover:bg-surface" data-testid="group-members-close"><X size={16} /></button>
                        </div>

                        {/* Owner pill */}
                        <div className="mt-3 flex items-center gap-2 p-2 bg-orange/10 border border-orange/30">
                            <ShieldCheck size={14} className="text-orange" />
                            <span className="text-xs font-mono">
                                Créateur : <strong>{detailById[group.created_by]?.name || "Vous"}</strong>
                                {group.created_by === user.id && <span className="ml-1 pill bg-orange text-white !px-1.5 !py-0 text-[10px]">Vous</span>}
                            </span>
                        </div>

                        {isOwner && (
                            <>
                                <div className="mt-3 relative">
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
                                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un contact…" className="field-input !pl-8" data-testid="group-members-search" />
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {NIVEAU_TABS.map((t) => {
                                        const isActive = niveauFilter === t.key;
                                        return (
                                            <button key={t.key} type="button" onClick={() => setNiveauFilter(t.key)} className={`px-2.5 py-1 text-[11px] transition-colors ${isActive ? "bg-ink text-white border border-ink" : "bg-paper border border-rule hover:border-ink/40"}`} data-testid={`group-members-niveau-${t.key}`}>{t.label}</button>
                                        );
                                    })}
                                </div>
                                <div className="mt-2 text-[11px] font-mono text-muted2">{selected.length + 1} membre(s) au total · vous êtes inclus·e</div>
                            </>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto scroll-visible min-h-0">
                        {!isOwner ? (
                            // Read-only: list current members from members_detail
                            (group.members_detail || []).filter((d) => d.id !== group.created_by).map((d) => {
                                const Icon = ROLE_ICON[d.role] || GraduationCap;
                                return (
                                    <div key={d.id} className="px-5 py-3 border-b border-rule flex items-center gap-3" data-testid={`group-member-readonly-${d.id}`}>
                                        <div className="w-9 h-9 bg-orange/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-orange" /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{d.name}</div>
                                            <div className="text-[11px] font-mono text-muted2">{ROLE_LABEL[d.role] || d.role} · {d.email}</div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : filtered.length === 0 ? (
                            <div className="p-8 text-center text-xs text-muted2 font-mono">Aucun contact.</div>
                        ) : filtered.map((c) => {
                            const Icon = ROLE_ICON[c.role] || GraduationCap;
                            const isSelected = selected.includes(c.id);
                            const niveauLabel = c.niveau === "1ere" ? "1ère" : c.niveau === "2eme" ? "2ème" : null;
                            return (
                                <button type="button" key={c.id} onClick={() => toggle(c.id)} className={`w-full text-left px-5 py-3 border-b border-rule flex items-center gap-3 ${isSelected ? "bg-orange/10" : "hover:bg-surface"}`} data-testid={`group-member-toggle-${c.id}`}>
                                    <div className={`w-5 h-5 border-2 flex items-center justify-center ${isSelected ? "bg-orange border-orange" : "border-rule"}`}>
                                        {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                                    </div>
                                    <div className="w-9 h-9 bg-orange/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-orange" /></div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate flex items-center gap-2">
                                            {c.name}
                                            {niveauLabel && <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{niveauLabel}</span>}
                                        </div>
                                        <div className="text-[11px] font-mono text-muted2">{ROLE_LABEL[c.role] || c.role} · {c.email}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="p-3 sm:p-4 border-t border-rule flex justify-end gap-2 bg-paper shrink-0">
                        <button type="button" onClick={onClose} className="btn-outline !text-xs" data-testid="group-members-cancel">{isOwner ? "Annuler" : "Fermer"}</button>
                        {isOwner && (
                            <button type="submit" disabled={saving} className="btn-primary !text-xs" data-testid="group-members-save">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
                                Enregistrer ({selected.length + 1} membres)
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
